// Core packages
import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { Subscription } from 'rxjs';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import MappingIndex from 'src/app/modules/properties/components/mapping-index.interface';
import { SettingsService } from "src/app/modules/settings/settings.service";
import { PropertyInterface } from 'src/app/shared/models/property/property.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';


@Component({
  selector: 'app-view-structure-dialog',
  templateUrl: './view-structure-dialog.component.html',
  styleUrls: ['./view-structure-dialog.component.scss']
})
export class ViewStructureDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.send'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-paper-plane',
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;
  formIsLoading = false;
  mappingIndex: MappingIndex[] = [];
  condoStructure: any;
  formActiveSubitemId = "";
  formActiveSection = "";
  sectionPlant = false;
  activeItem: MappingIndex;
  activeSubitem: any;
  property: PropertyInterface;
  dialog: any;

  condoStructureTemplateId = "";
  condoStructureTemplate: any[] = [];
  
  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ViewStructureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private translateService: TranslateService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private settingsService: SettingsService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    this.dialog = document.getElementsByClassName("mat-dialog-content")[0];

    if (this.data && this.data.propertyData) {
      this.property = this.data.propertyData;
    }

    if (this.property) {
      this.subscriptions.push(
        this.settingsService.getStructure().subscribe(
          (res) => {
            if (res.status) {
              if (
                res.condoStructureTemplate &&
                Array.isArray(res.condoStructureTemplate) &&
                res.condoStructureTemplate.length > 0 &&
                res.condoStructureTemplate[0].area &&
                Array.isArray(res.condoStructureTemplate[0].area)
              ) {
                this.condoStructureTemplateId = res.condoStructureTemplate[0]._id;
                this.condoStructureTemplate = res.condoStructureTemplate[0].area;
  
                this.mappingIndex = this.condoStructureTemplate;
                
                this.mappingIndex = this.translateKeys2(
                  ["placeholder", "label"],
                  this.mappingIndex
                );
                this.checkPropertyType();

                console.log("this.mappingIndex", this.mappingIndex)
              }
            }
          })
      );
    }

    if (this.data && this.data.condoStructure) {
      this.condoStructure = this.data.condoStructure;
    }

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => (this.loggedUser = res)));
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );
    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });

    if (!this.currentRole.propertiesPermission) {
      this.currentRole.propertiesPermission = {
        amiantoDoc: false,
        amminImmobile: false,
        antincendio: false,
        ascensoriEMontauto: false,
        contratti: false,
        edile: false,
        expiresUnlimited: false,
        gestionaleAmministrativo: false,
        impiantoElettrico: false,
        impiantiElettronici: false,
        impiantiRicreativiSportivi: false,
        impiantiSpeciali: false,
        lineaVita: false,
        mappatura: false,
        riscaldamentoCondizionamento: false,
        trattamentoAcqua: false,
        unitaImmobiliari: false,
        unlimited: false,
        gestionaleContabile: false,
        urbanisticaEdEdilizia: false,
        catasto: false,
        preventivi: false,
      }
    }

    // Stop loading dialog
    setTimeout(() => {
      this.dialogIsLoading = false;
    }, 1000);
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle after view init cycle hook
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Leave this code under ngAfterViewInit to prevent expressionChangedAfterChecked error
  }

  /**
   * Handle property type check
   *
   * @since 1.0.0
   */
  checkPropertyType() {
    // Check property type: Condo or BSC
    if (this.property.type === "bsc") {
      const strutture = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.strutture";
      });
      if (strutture
        && strutture.content
        && Array.isArray(strutture.content)
      ) {
        let index = strutture.content.findIndex((item: any) => {
          return item.key === "condominiBsc"
        });
        strutture.content[index].hidden = false;
        index = strutture.content.findIndex((item: any) => {
          return item.key === "fabbricatoAbitativo"
        });
        strutture.content[index].hidden = true;
        index = strutture.content.findIndex((item: any) => {
          return item.key === "struttureFabbindip"
        });
        strutture.content[index].hidden = false;
      }

      const trattamentoAcqua = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.trattamentoAcqua";
      });
      if (trattamentoAcqua
        && trattamentoAcqua.content
        && Array.isArray(trattamentoAcqua.content)
      ) {
        const index = trattamentoAcqua.content.findIndex((item: any) => {
          return item.key === "sottostazioneAltroCondBsc"
        });
        trattamentoAcqua.content[index].hidden = true;
      }

      const riscaldamentoCondizionamento = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.riscaldamentoCondizionamento";
      });
      if (riscaldamentoCondizionamento
        && riscaldamentoCondizionamento.content
        && Array.isArray(riscaldamentoCondizionamento.content)
      ) {
        // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
        //   return item.key === "sottostazioniCt"
        // });
        // riscaldamentoCondizionamento.content[index].hidden = true;
        // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
        //   return item.key === "centraleRicervimento"
        // });
        // riscaldamentoCondizionamento.content[index].hidden = true;
        const index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
          return item.key === "cannaCollettiva"
        });
        riscaldamentoCondizionamento.content[index].hidden = true;
      }

      const dispositiviAntincendio = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.dispositiviAntincendio";
      });
      if (dispositiviAntincendio
        && dispositiviAntincendio.content
        && Array.isArray(dispositiviAntincendio.content)
      ) {
        const index = dispositiviAntincendio.content.findIndex((item: any) => {
          return item.key === "evac"
        });
        dispositiviAntincendio.content[index].hidden = true;
      }
    } else {
      const strutture = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.strutture";
      });
      if (strutture
        && strutture.content
        && Array.isArray(strutture.content)
      ) {
        let index = strutture.content.findIndex((item: any) => {
          return item.key === "condominiBsc"
        });
        strutture.content[index].hidden = true;
        index = strutture.content.findIndex((item: any) => {
          return item.key === "fabbricatoAbitativo"
        });
        strutture.content[index].hidden = false;
        index = strutture.content.findIndex((item: any) => {
          return item.key === "struttureFabbindip"
        });
        strutture.content[index].hidden = false;
      }

      const trattamentoAcqua = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.trattamentoAcqua";
      });
      if (trattamentoAcqua
        && trattamentoAcqua.content
        && Array.isArray(trattamentoAcqua.content)
      ) {
        const index = trattamentoAcqua.content.findIndex((item: any) => {
          return item.key === "sottostazioneAltroCondBsc"
        });
        trattamentoAcqua.content[index].hidden = false;
      }

      const riscaldamentoCondizionamento = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.riscaldamentoCondizionamento";
      });
      if (riscaldamentoCondizionamento
        && riscaldamentoCondizionamento.content
        && Array.isArray(riscaldamentoCondizionamento.content)
      ) {
        // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
        //   return item.key === "sottostazioniCt"
        // });
        // riscaldamentoCondizionamento.content[index].hidden = false;
        // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
        //   return item.key === "centraleRicervimento"
        // });
        // riscaldamentoCondizionamento.content[index].hidden = false;
        const index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
          return item.key === "cannaCollettiva"
        });
        riscaldamentoCondizionamento.content[index].hidden = false;
      }

      const dispositiviAntincendio = this.mappingIndex.find((el: any) => {
        return el.title === "properties.structures.dispositiviAntincendio";
      });
      if (dispositiviAntincendio
        && dispositiviAntincendio.content
        && Array.isArray(dispositiviAntincendio.content)
      ) {
        const index = dispositiviAntincendio.content.findIndex((item: any) => {
          return item.key === "evac"
        });
        dispositiviAntincendio.content[index].hidden = false;
      }
    }
  }

  /**
   * Check if given macr-section item is visibile or not depending on
   * structure open status. If structure is open, than this will always returns true
   * If it's false, this will check if at least one sub-section has one or more
   * values. If so, return true, otherwise return false;
   *
   * @since 1.0.0
   */
  checkVisibility(item: any): boolean {
    const condoStructureClose = true;
    if (condoStructureClose) {
      if (item.content && item.content.length) {
        // Let's make anagrafica section ALWAYS visible
        if (item.content[0].key === "anagrafica") {
          return true;
        }
        return item.content.some(
          (subitem: MappingIndex) => this.getChildren(subitem.key).length
        );
      }
      return false;
    }
    return true;
  }

  /**
   * Get children (existing values) for requested section
   *
   * @since 1.0.0
   */
  getChildren(key: any): any[] {
    if (typeof this.condoStructure !== "undefined"
      && typeof this.condoStructure[key] !== "undefined") {
      if (key === "anagrafica") {
        return [];
      } else {
        if (Array.isArray(this.condoStructure[key])) {
          let structures = JSON.parse(
            JSON.stringify(this.condoStructure[key])
          );
          structures = structures.filter(el => el.active);
          return structures;
        } else {
          return [];
        }
      }
    }
    return [];
  }

  /**
   * Navigate to given section inside the same property detail page
   *
   * @since 1.0.0
   */
  activateSection(rawSection: string): void {
    this.dialog.scrollTo({
      top: 0,
      behavior: "smooth"
    });

    // Reset data and call the new data form
    this.formActiveSubitemId = undefined;

    const controls = this.form.controls;
    for (const key in controls) {
      this.form.removeControl(key);
    }

    const section = rawSection.replace(":structureId", this.property.structure);
    this.formActiveSubitemId = "";
    this.formActiveSection = section;

    this.updateFormSection();
  }

  /**
   * Handle form item click and open view/edit popup
   *
   * @since 1.0.0
   */
  onFormItemClick(rawSection: string, item: any): void {
    this.dialog.scrollTo({
      top: 0,
      behavior: "smooth"
    });

    const section = rawSection.replace(":structureId", this.property.structure);
    this.formActiveSubitemId = item._id;
    this.formActiveSection = section;

    this.updateFormSection();
  }

  /**
   * Update form section depending on route params
   *
   * @since 1.0.0
   */
  updateFormSection(): void {
    // Reset model to prevent import previous form data
    if (this.model) {
      for (const property in this.model) {
        delete this.model[property];
      }
    }

    this.dialog.scrollTo({
      top: 0,
      behavior: "smooth"
    });

    // Reset data and call the new data form
    if (this.form) {
      this.options.formState = {
        disabled: true
      };
    }

    // Add section param to routing, which is used in connections type
    const params = Object.assign({}, this.activatedRoute.snapshot.params, { selected: false });
    params.mappingFormActiveSection = this.formActiveSection;
    this.activatedRoute.snapshot.params = params;

    if (this.formActiveSection) {
      this.formIsLoading = true; // Start form loading

      // BEGIN - Set open status to current mapping index
      const origFormActiveSection = this.formActiveSection.replace(
        this.property.structure,
        ":structureId"
      );

      this.mappingIndex.map((item: MappingIndex) => {
        item.content.map((subItem: MappingIndex) => {
          if (subItem.section === origFormActiveSection) {
            // Get currente element is a Plant
            // delete note field from form
            if (subItem.formConfig
              && Array.isArray(subItem.formConfig)
              && subItem.formConfig.length) {
              if (subItem.formConfig[0].fieldGroup
                && Array.isArray(subItem.formConfig[0].fieldGroup)
                && subItem.formConfig[0].fieldGroup.length) {
                const index = subItem.formConfig[0].fieldGroup.findIndex((el: any) => {
                  return el.key === "note";
                })
                if (index > -1) {
                  subItem.formConfig[0].fieldGroup.splice(index, 1);
                }
              }
            }
            if (
              item.title === "properties.structures.impiantiElettrici" ||
              item.title === "properties.structures.trattamentoAcqua" ||
              item.title ===
              "properties.structures.riscaldamentoCondizionamento" ||
              item.title === "properties.structures.dispositiviAntincendio" ||
              item.title === "properties.structures.ascensoriEMontauto" ||
              item.title === "properties.structures.impiantiElettronici" ||
              item.title === "properties.structures.lineeVita" ||
              item.title === "properties.structures.impiantiSpeciali" ||
              item.title === "properties.structures.impiantiRicreativi"
            ) {
              this.sectionPlant = true;
            } else {
              this.sectionPlant = false;
            }

            item.expanded = true;
            subItem.active = true;
            this.activeItem = subItem;
            if (
              this.formActiveSection &&
              origFormActiveSection !==
              "condo-structures/:structureId/anagrafica"
            ) {
              const children = this.getChildren(subItem.key);
              if (children.length) {
                this.activeSubitem = children.find(
                  (val: any) => val._id === this.formActiveSubitemId
                );

                if (this.activeSubitem && this.activeSubitem.files) {
                  if (this.form.controls.files) {
                    this.form.controls.files.setValue(this.activeSubitem.files);
                  }
                  this.form.updateValueAndValidity();
                }
                this.fields = subItem.formConfig;

                if (this.activeSubitem) {
                  this.model = this.activeSubitem;
                } else {
                  // Add new subitem. Default name from section
                  const tot = children.length + 1;
                  const nome =
                    this.translateService.instant(subItem.title) + " " + tot;
                  this.model = { nome };
                }
              } else {
                // New first subitem. Default name from section
                this.fields = subItem.formConfig;
                const nome =
                  this.translateService.instant(subItem.title) + " 1";
                this.model = { nome };
              }
            } else if (
              subItem.section === "condo-structures/:structureId/anagrafica"
            ) {
              this.activeSubitem = this.condoStructure.anagrafica;
              // Anagrafica: is not completed
              if (typeof this.activeSubitem === "undefined") {
                this.activeSubitem = {
                  active: true
                };
              }
              this.fields = subItem.formConfig;
              this.model = this.activeSubitem;
              this.formActiveSubitemId = Math.random().toString();
            }
          } else {
            subItem.active = false;
          }
        });
      });

      if (this.form && !this.condoStructure.open) {
        this.options.formState = {
          disabled: true
        };
      }
      // END - Set open status to current mapping index

      setTimeout(() => {
        this.formIsLoading = false;
      }, 300);

    } else {
      // Reset the last item.expanded mappingIndex to close tree from previous structure
      if (Array.isArray(this.mappingIndex) && this.mappingIndex.length) {
        this.mappingIndex.forEach((item: any) => {
          if (item.expanded) {
            item.expanded = false;
            if (Array.isArray(item.content) && item.content.length) {
              item.content.forEach((elem: any) => {
                if (elem.active) {
                  elem.active = false;
                }
              });
            }
          }
        });
      }
    }
  }

  /**
   * Translate all keys with given labels inside given object and returns it
   *
   * @since 1.0.0
   */
  translateKeys(keys: string[], obj: any): any {
    if (Array.isArray(obj)) {
      obj.forEach((item: MappingIndex) => {
        for (const prop in item) {
          if (item.hasOwnProperty(prop)) {
            if (keys.includes(prop)) {
              item[prop] = this.translateService.instant(item[prop]);
            }
            if (typeof item[prop] === "object") {
              item[prop] = this.translateKeys(keys, item[prop]);
            }
          }
        }
      });
    } else {
      for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          if (keys.includes(prop) && obj[prop]) {
            obj[prop] = this.translateService.instant(obj[prop]);
          }
          if (typeof obj[prop] === "object") {
            obj[prop] = this.translateKeys(keys, obj[prop]);
          }
        }
      }
    }
    return obj;
  }
  translateKeys2(keys: string[], obj: any): any {
    if (Array.isArray(obj)) {
      obj.forEach((item: MappingIndex) => {
        for (const prop in item) {
          if (prop === "hooks") {
            const propKey = item[prop];
            item[prop] = {
              onInit(field: any) {
                field.defaultValue = `${propKey} ${+field.parent.key + 1}`;
              },
            };
          }
          if (prop === "hideExpression" && item[prop]) {
            const propKey = item[prop].key;
            const propCompareValue = item[prop].value;
            if (item[prop].operator === "!==") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                const compareValue = propCompareValue;
                return currentValue !== compareValue;
              }
            } else if (item[prop].operator === "!") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                return !currentValue;
              }
            } else if (item[prop].operator === "!includes") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                const compareValue = propCompareValue;
                return typeof currentValue === "undefined" || !currentValue.includes(compareValue);
              }
            }
          }
          if (prop === "expressionProperties" && item[prop]) {
            const propDisabled = item[prop].disabled;
            
            if (typeof item[prop].required !== "undefined") {
              const propKey = item[prop].required.key;
              const propCompareValue = item[prop].required.value;
              if (item[prop].required.operator === "===") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    const compareValue = propCompareValue;
                    return currentValue === compareValue;
                  }
                }
              } else if (item[prop].required.operator === "!") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    return !currentValue;
                  }
                }
              } else if (item[prop].required.operator === "") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    return currentValue;
                  }
                }
              }
            } else {
              item[prop] = {
                "templateOptions.disabled": propDisabled,
              }
            }
          }
          if (item.hasOwnProperty(prop)) {
            if (keys.includes(prop)) {
              if (item["translation"]) {
                item[prop] = item["translation"].it;
              }
            }
            if (typeof item[prop] === "object") {
              item[prop] = this.translateKeys2(keys, item[prop]);
            }
          }
        }
      });
    } else {
      for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          if (prop === "options" && Array.isArray(obj[prop])) {
            obj[prop].forEach((elem: any) => {
              if (elem.value && elem.value === "true") {
                elem.value = true;
              } else if (elem.value && elem.value === "false") {
                elem.value = false;
              }
            })
          }
          if (keys.includes(prop) && obj[prop]) {
            obj[prop] = this.translateService.instant(obj[prop]);
            if (prop === "label" && obj["translationLabel"]) {
              obj[prop] = obj["translationLabel"].it;
            } else if (prop === "placeholder" && obj["translationPlaceholder"]) {
              obj[prop] = obj["translationPlaceholder"].it;
            }
          }
          if (typeof obj[prop] === "object") {
            obj[prop] = this.translateKeys2(keys, obj[prop]);
          }
        }
      }
    }
    return obj;
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Handle panel header open/close
   *
   * @since 1.0.0
   *
   */
  onPanelClicked(item: any, index: number): void {
    item.expanded = !item.expanded;
    this.mappingIndex.forEach((el: MappingIndex, i: number) => {
      if (i !== index) {
        el.expanded = false;
      }
    });

    // Handle scroll to panel
    if (item.expanded) {
      const headerHeight = 60;
      const top = (headerHeight * (index + 1)) - headerHeight;
      this.dialog.scrollTo({
        top,
        behavior: "smooth"
      });
    }
  }
}
